import React, { useState, useEffect, useRef, useMemo } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import Select from "react-select";
import { supabase } from "../../supabaseClient";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../../assets/css/checklist.css";
import ImageResize from "quill-image-resize-module-react";
import MicButton from "./MicButton";
import { useTimezone } from "../../Context/TimezoneContext";
import { toLocalInputValue, toUtcIsoFromLocalInput } from "../../utils/taskDateTime";

// Register image resize module with Quill
Quill.register("modules/imageResize", ImageResize);

function EditPopup({
  showDueDate,
  show,
  handleClose,
  handleSave,
  item,
  addTask,
  categoriesData,
  fetchSubcategories,
  subcategoriesData,
  titleAction,
  handleDelete,
  sharedCategories = [],
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState(""); // Add state for due date
  const [errorMessage, setErrorMessage] = useState(""); // Add state for error message
  const [categories, setCategories] = useState(categoriesData);
  const [subcategories, setSubcategories] = useState(subcategoriesData || []);
  const [selectedCategory, setSelectedCategory] = useState("new");
  const [selectedSubcategory, setSelectedSubcategory] = useState("null");
  const [newCategory, setNewCategory] = useState("");

  const [newSubcategory, setNewSubcategory] = useState("");
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(true);
  const [showNewSubcategoryInput, setShowNewSubcategoryInput] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { userTimezone } = useTimezone();
  useEffect(() => {
    setCategories(categoriesData);
    setSubcategories(subcategoriesData || []);
  }, [categoriesData, subcategoriesData]);

  const textareaRef = useRef(null);
  const [isManualInputResize, setIsManualInputResize] = useState(false);
  const [inputTextareaHeight, setInputTextareaHeight] = useState(88);

  // Quill modules configuration
  const quillModules = useMemo(() => ({
    toolbar: [
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
      [{ align: [] }],
      [{ size: ["small", false, "large", "huge"] }],
      ["link"],
      ["code-block"],
      ["clean"],
    ],
    imageResize: {
      parchment: Quill.import("parchment"),
      modules: ["Resize", "DisplaySize"],
    },
  }), []);

  // Quill formats
  const quillFormats = [
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "align",
    "size",
    "link",
    "image",
    "code-block",
  ];
  const handleCategoryChange = async (e) => {
    const categoryId = e.target.value;
    console.log("[EditPopup] Category changed to:", categoryId);

    if (categoryId === "new") {
      setShowNewCategoryInput(true);
      setSelectedCategory("");
      setSubcategories([]);
      setSelectedSubcategory("null");
      setShowNewSubcategoryInput(false);
      setNewCategory(""); // Reset new category input
    } else {
      setShowNewCategoryInput(false);
      // Ensure consistent type - store as the original type from the dropdown
      setSelectedCategory(categoryId);
      setNewCategory(""); // Clear any previous new category text

      if (categoryId && categoryId !== "0") {
        const fetchedSubcategories = await fetchSubcategories(categoryId);
        setSubcategories(fetchedSubcategories || []);
      } else {
        setSubcategories([]);
      }
      setSelectedSubcategory("null");
      setShowNewSubcategoryInput(false);
    }
    setErrorMessage(""); // Clear any previous error
  };

  const handleSubcategoryChange = (e) => {
    const subcategoryId = e.target.value;
    if (subcategoryId === "new") {
      setShowNewSubcategoryInput(true);
      setSelectedSubcategory("new");
    } else if (subcategoryId === "null") {
      setShowNewSubcategoryInput(false);
      setSelectedSubcategory(null);
    } else {
      setShowNewSubcategoryInput(false);
      setSelectedSubcategory(subcategoryId);
    }
  };
  useEffect(() => {
    if (addTask) {
      setTitle("");
      setDescription("");
      setDueDate("");
      setNewCategory("");
      setNewSubcategory("");
      setShowNewCategoryInput(true);
      setShowNewSubcategoryInput(false);
    }
    if (item) {
      console.log("category id", item.category_id);
      setTitle(item.title || "");
      setDescription(item.description || "");
      setDueDate(showDueDate ? toLocalInputValue(item.due_date, userTimezone) : "");

      // Reset new category/subcategory input fields
      setNewCategory("");
      setNewSubcategory("");
      setShowNewSubcategoryInput(false);
      setErrorMessage("");

      // Handle category selection
      if (item.category_id) {
        // First, set showNewCategoryInput to false since we have a category
        setShowNewCategoryInput(false);

        // Check if category_id matches a shared category
        const matchedShared = sharedCategories.find(
          (sc) => String(sc.originalId || sc.id.replace('shared_', '')) === String(item.category_id)
        );
        if (matchedShared) {
          setSelectedCategory(matchedShared.id);
          setSelectedSubcategory("null");
          return;
        }

        // Check if category_id is in main categories list
        const isMainCategory = categories?.some(
          (category) => String(category.id) === String(item.category_id)
        );

        if (isMainCategory) {
          // It's a main category - select it directly
          console.log("Selected Category (main):", item.category_id);
          setSelectedCategory(item.category_id);
          setSelectedSubcategory("null");
          // Fetch subcategories for this category
          if (fetchSubcategories) {
            fetchSubcategories(item.category_id).then((fetchedSubs) => {
              setSubcategories(fetchedSubs || []);
            });
          }
        } else if (item._shared || item._sent) {
          // For shared/sent items, use the categoriesData passed from parent
          // (Supabase RLS blocks cross-user category lookups with anon key)
          setSelectedCategory(categories?.[0]?.id || "0");
          if (subcategoriesData?.length > 0) {
            setSubcategories(subcategoriesData);
            setSelectedSubcategory(item.category_id);
          } else {
            setSelectedSubcategory("null");
          }
        } else {
          // It's a subcategory - need to find parent
          let getParentCategory = async () => {
            const categoryTable = 
              titleAction === "Lists" ? "lists_categories" :
              titleAction === "Notes" ? "notes_categories" :
              titleAction === "Grocery" ? "grocery_categories" :
              "categories";
            
            const { data, error } = await supabase
              .from(categoryTable)
              .select("parent_category_id")
              .eq("id", item.category_id);
            if (error) {
              throw error;
            }

            if (data.length > 0 && data[0].parent_category_id) {
              const fetchedSubcategories = await fetchSubcategories(
                data[0].parent_category_id
              );
              setSubcategories(fetchedSubcategories || []);
              setSelectedCategory(data[0].parent_category_id);
              setSelectedSubcategory(item.category_id);
              console.log("Selected Category (parent):", data[0].parent_category_id);
              console.log("Selected Subcategory:", item.category_id);
            } else {
              // No parent found, treat as main category
              setSelectedCategory(item.category_id);
              setSelectedSubcategory("null");
            }
          };
          getParentCategory();
        }
      } else {
        // No category - show as "General" (value "0")
        setShowNewCategoryInput(false);
        setSelectedCategory("0");
        setSelectedSubcategory("null");
        console.log("No category - defaulting to General");
      }
    }
  }, [item, categories, showDueDate, userTimezone]);

  const handleSaveClick = async () => {
    if (!title.trim()) {
      setErrorMessage("Title is required.");
      return;
    }

    // Skip category validation for shared/sent items (category is read-only)
    const isSharedOrSent = item?._shared || item?._sent;
    if (!isSharedOrSent) {
      // Bug 13/15 fix: Validate category - either creating new or selecting existing
      if (showNewCategoryInput && !newCategory.trim()) {
        setErrorMessage("Category name is required.");
        return;
      }
      // Bug 13/15 fix: Also validate when not creating new category
      if (!showNewCategoryInput && !selectedCategory) {
        setErrorMessage("Please select a category.");
        return;
      }
    }

    // Validate new subcategory name if creating new subcategory
    if (showNewSubcategoryInput && selectedSubcategory === "new" && !newSubcategory.trim()) {
      setErrorMessage("Subcategory name is required.");
      return;
    }

    if (showDueDate && dueDate && !validateDueDate()) {
      return;
    }

    setErrorMessage("");
    setIsSaving(true);

    try {
      // Bug 13/15 fix: Determine the category value - either new name or selected ID
      // When showNewCategoryInput is true, use newCategory (string name for new category)
      // When showNewCategoryInput is false, use selectedCategory (ID of existing category)
      const categoryValue = showNewCategoryInput ? newCategory : selectedCategory;
      
      // Debug log to verify category value
      console.log("[EditPopup] Saving with category:", {
        showNewCategoryInput,
        newCategory,
        selectedCategory,
        categoryValue,
      });

      // Determine subcategory value - either ID, new name, or null
      let subcategoryValue = null;
      if (selectedSubcategory === "new" && showNewSubcategoryInput) {
        subcategoryValue = newSubcategory;
      } else if (selectedSubcategory && selectedSubcategory !== "null" && selectedSubcategory !== "new") {
        subcategoryValue = selectedSubcategory;
      }

      await handleSave({
        ...item,
        title,
        description,
        due_date: dueDate || null,
        category_id: categoryValue,
        subcategory_id: subcategoryValue,
        isNewCategory: showNewCategoryInput,
        isNewSubcategory: showNewSubcategoryInput && selectedSubcategory === "new",
      });
    } catch (error) {
      console.error("Error saving:", error);
      setErrorMessage("Failed to save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };
  const validateDueDate = () => {
    if (!dueDate) {
      setErrorMessage("");
      return true;
    }

    const normalizedDueDate = toUtcIsoFromLocalInput(dueDate, userTimezone);
    if (!normalizedDueDate) {
      setErrorMessage("Invalid due date and time.");
      return false;
    }

    if (new Date(normalizedDueDate).getTime() < Date.now()) {
      setErrorMessage("Due date and time cannot be in the past.");
      return false;
    }
    setErrorMessage("");
    return true;
  };

  // Reset input fields when modal closes
  const handleModalClose = () => {
    setNewCategory("");
    setNewSubcategory("");
    setShowNewSubcategoryInput(false);
    setErrorMessage("");
    setIsSaving(false);
    handleClose();
  };

  // Build category options for React-Select
  const categoryOptions = [
    { value: "new", label: "+ Create New Category" },
    { value: "0", label: "General" },
    ...(categories?.map((category) => ({
      value: category.id,
      label: category.name,
    })) || []),
    ...sharedCategories.map((sc) => ({
      value: sc.id,
      label: sc.name.replace(" (shared)", ""),
      isShared: true,
    })),
  ];

  // Build subcategory options for React-Select
  const subcategoryOptions = [
    { value: "null", label: "No Subcategory" },
    { value: "new", label: "+ Create New Subcategory" },
    ...subcategories.map((subcategory) => ({
      value: subcategory.id,
      label: subcategory.name,
    })),
  ];

  // Get current selected category value for React-Select
  const getCurrentCategoryValue = () => {
    if (showNewCategoryInput)
      return { value: "new", label: "+ Create New Category" };
    if (selectedCategory === "0") return { value: "0", label: "General" };
    // Check own categories
    const cat = categories?.find((c) => String(c.id) === String(selectedCategory));
    if (cat) return { value: cat.id, label: cat.name };
    // Check shared categories
    const shared = sharedCategories.find((sc) => String(sc.id) === String(selectedCategory));
    if (shared) return { value: shared.id, label: shared.name.replace(" (shared)", ""), isShared: true };
    return { value: "new", label: "+ Create New Category" };
  };

  // Get current selected subcategory value for React-Select
  const getCurrentSubcategoryValue = () => {
    if (selectedSubcategory === "null" || selectedSubcategory === null)
      return { value: "null", label: "No Subcategory" };
    if (selectedSubcategory === "new")
      return { value: "new", label: "+ Create New Subcategory" };
    // Use loose equality to handle number/string type differences
    const subcat = subcategories.find((s) => String(s.id) === String(selectedSubcategory));
    return subcat
      ? { value: subcat.id, label: subcat.name }
      : { value: "null", label: "No Subcategory" };
  };

  return (
    <div className="bg-overlay-height">
      <Modal
        backdropClassName="bg-overlay-hide"
        show={show}
        onHide={handleModalClose}
        size="lg"
        className="task-popup"
        centered
      >
        <Modal.Header closeButton>
          <div className="d-flex justify-content-between w-100 align-items-center">
            <Modal.Title>Edit {titleAction?.slice(0, -1) || "Item"}</Modal.Title>
            <Button
              title={item?._shared ? 'No authority' : item?._sent ? 'Unshare to use' : 'Delete Item'}
              className="btn-popup-delete"
              disabled={item?._shared || item?._sent}
              onClick={() => {
                if (item?._shared || item?._sent) return;
                if (handleDelete) handleDelete(item?.id ?? null);
                handleModalClose();
              }}
              style={item?._shared || item?._sent ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
            >
              <i className="bi bi-trash me-2"></i>
              Delete
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body>
          {/* Title Input */}
          <Form.Group controlId="formTitle" className="mb-4">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Form.Group>

          {/* Category & Subcategory Row */}
          <div className="category-row">
            <Form.Group controlId="formCategory" className="mb-4 w-100">
              <Form.Label>Category</Form.Label>
              {(item?._shared || item?._sent) ? (
                <div className="d-flex align-items-center gap-2" style={{
                  padding: "8px 12px", borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  background: "var(--background)",
                  fontSize: "0.85rem", color: "var(--text)",
                }}>
                  {item?._shared && <i className="bi bi-box-arrow-in-down-right" style={{ fontSize: '0.75rem', color: 'var(--teal-bg)' }}></i>}
                  {item?._sent && <i className="bi bi-box-arrow-up-right" style={{ fontSize: '0.75rem', color: '#f59e0b' }}></i>}
                  <span>{getCurrentCategoryValue().label || "General"}</span>
                </div>
              ) : (
                <>
                  <Select
                    className="popup-select-container"
                    classNamePrefix="popup-select"
                    options={categoryOptions}
                    value={getCurrentCategoryValue()}
                    onChange={(option) =>
                      handleCategoryChange({ target: { value: option?.value || "new" } })
                    }
                    formatOptionLabel={(option) => (
                      <div className="d-flex align-items-center gap-1">
                        {option.isShared && <i className="bi bi-box-arrow-in-down-right" style={{ fontSize: '0.7rem', color: 'var(--teal-bg)' }}></i>}
                        <span>{option.label}</span>
                      </div>
                    )}
                    isSearchable={false}
                    placeholder="Select Category"
                  />
                  {showNewCategoryInput && (
                    <Form.Control
                      type="text"
                      placeholder="Enter new category name"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      className="mt-3"
                    />
                  )}
                </>
              )}
            </Form.Group>

            {selectedCategory !== "0" && (
              <Form.Group controlId="formSubcategory" className="mb-4 w-100">
                <Form.Label>Subcategory</Form.Label>
                {(item?._shared || item?._sent) ? (
                  <div style={{
                    padding: "8px 12px", borderRadius: "8px",
                    border: "1px solid var(--border-color)",
                    background: "var(--background)",
                    fontSize: "0.85rem", color: "var(--text)", opacity: 0.6,
                  }}>
                    {getCurrentSubcategoryValue()?.label || "No Subcategory"}
                  </div>
                ) : (
                  <Select
                    className="popup-select-container"
                    classNamePrefix="popup-select"
                    options={subcategoryOptions}
                    value={getCurrentSubcategoryValue()}
                    onChange={(option) =>
                      handleSubcategoryChange({ target: { value: option?.value || "null" } })
                    }
                    isSearchable={false}
                    placeholder="Select Subcategory"
                  />
                )}
                {showNewSubcategoryInput && selectedSubcategory !== null && (
                  <Form.Control
                    type="text"
                    placeholder="Enter new subcategory name"
                    value={newSubcategory}
                    onChange={(e) => setNewSubcategory(e.target.value)}
                    className="mt-3"
                  />
                )}
              </Form.Group>
            )}
          </div>

          {/* Description with Quill Editor */}
          <Form.Group controlId="formDescription" className="mb-4">
            <Form.Label>Description</Form.Label>
            <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <ReactQuill
                  theme="snow"
                  value={description || ""}
                  onChange={(value) => setDescription(value)}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter description..."
                  style={{
                    marginBottom: 10,
                  }}
                />
              </div>
              <MicButton
                onTranscript={(text) => setDescription(prev => prev ? `${prev} ${text}` : text)}
                size="md"
              />
            </div>
          </Form.Group>

          {/* Due Date - Only for Tasks */}
          {titleAction === "Tasks" && (
            <Form.Group controlId="formDueDate" className="mb-4 ">
              <Form.Label>Due Date</Form.Label>
              <div className="date-input-wrapper">
                <Form.Control
                  type="datetime-local"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
                <i className="bi bi-calendar date-icon"></i>
              </div>
            </Form.Group>
          )}

          {errorMessage && (
            <Alert variant="danger" dismissible onClose={() => setErrorMessage("")}>
              {errorMessage}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="btn-popup-cancel"
            onClick={handleModalClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            className="btn-popup-save"
            onClick={handleSaveClick}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default EditPopup;
